import React, { useEffect, useState } from 'react';
import { Receipt, Loader2, Check, AlertCircle } from 'lucide-react';
import { getBillingProfile, setBillingProfile } from '../../services/supabase';

interface UserBillingCardProps {
  userId: string;
  userEmail: string;
}

/**
 * Superadmin card (inside the user detail panel) to put a client on monthly
 * invoicing or back on prepaid credits, with optional account details and a
 * negotiated flat per-plan rate. Reads/writes via the superadmin-gated RPCs.
 */
const UserBillingCard: React.FC<UserBillingCardProps> = ({ userId, userEmail }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const [billingMode, setBillingMode] = useState<'prepaid' | 'monthly_invoice'>('prepaid');
  const [invoiceCompany, setInvoiceCompany] = useState('');
  const [invoiceEmail, setInvoiceEmail] = useState('');
  const [customRate, setCustomRate] = useState(''); // pounds, e.g. "15" or "15.00"

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    getBillingProfile(userId).then((p) => {
      if (cancelled) return;
      if (!p.success) {
        setError(p.error || 'Failed to load billing profile');
      } else {
        setBillingMode(p.billing_mode === 'monthly_invoice' ? 'monthly_invoice' : 'prepaid');
        setInvoiceCompany(p.invoice_company || '');
        setInvoiceEmail(p.invoice_email || '');
        setCustomRate(p.custom_plan_rate_pence != null ? (p.custom_plan_rate_pence / 100).toString() : '');
      }
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [userId]);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSaved(false);

    let customRatePence: number | null = null;
    if (billingMode === 'monthly_invoice' && customRate.trim() !== '') {
      const pounds = Number(customRate);
      if (Number.isNaN(pounds) || pounds < 0) {
        setError('Custom rate must be a positive amount.');
        setSaving(false);
        return;
      }
      customRatePence = Math.round(pounds * 100);
    }

    const res = await setBillingProfile({
      userId,
      billingMode,
      invoiceCompany: invoiceCompany.trim() || null,
      invoiceEmail: invoiceEmail.trim() || invoiceEmail || null,
      customRatePence,
    });

    if (!res.success) {
      setError(res.error || 'Failed to save');
    } else {
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    }
    setSaving(false);
  };

  const isInvoice = billingMode === 'monthly_invoice';

  return (
    <div className="bg-slate-900/60 border border-slate-700/60 rounded-xl p-4">
      <div className="flex items-center gap-2 mb-3">
        <Receipt size={16} className="text-teal-400" />
        <h4 className="text-sm font-semibold text-white">Billing & invoicing</h4>
        {isInvoice && (
          <span className="ml-auto rounded-full bg-teal-500/15 text-teal-300 text-[11px] font-medium px-2 py-0.5">
            Monthly invoice
          </span>
        )}
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-slate-500 text-sm py-3">
          <Loader2 size={14} className="animate-spin" /> Loading…
        </div>
      ) : (
        <div className="space-y-3">
          {/* Mode toggle */}
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => setBillingMode('prepaid')}
              className={`p-2.5 rounded-lg border text-sm font-medium transition-all ${
                !isInvoice
                  ? 'border-teal-500/50 bg-teal-500/10 text-teal-300'
                  : 'border-slate-700 bg-slate-800/50 text-slate-400 hover:border-slate-600'
              }`}
            >
              Prepaid credits
            </button>
            <button
              type="button"
              onClick={() => setBillingMode('monthly_invoice')}
              className={`p-2.5 rounded-lg border text-sm font-medium transition-all ${
                isInvoice
                  ? 'border-teal-500/50 bg-teal-500/10 text-teal-300'
                  : 'border-slate-700 bg-slate-800/50 text-slate-400 hover:border-slate-600'
              }`}
            >
              Monthly invoice
            </button>
          </div>

          {isInvoice && (
            <div className="space-y-3 pt-1">
              <div>
                <label className="text-xs font-medium text-slate-400 mb-1 block">Company / account name</label>
                <input
                  type="text"
                  value={invoiceCompany}
                  onChange={(e) => setInvoiceCompany(e.target.value)}
                  placeholder="e.g. Acme Estate Agents Ltd"
                  className="w-full px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-white text-sm placeholder-slate-600 focus:outline-none focus:border-teal-500/50"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-400 mb-1 block">Invoice email</label>
                <input
                  type="email"
                  value={invoiceEmail}
                  onChange={(e) => setInvoiceEmail(e.target.value)}
                  placeholder={userEmail}
                  className="w-full px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-white text-sm placeholder-slate-600 focus:outline-none focus:border-teal-500/50"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-400 mb-1 block">
                  Custom per-plan rate (£) <span className="text-slate-600">— optional, overrides volume pricing</span>
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 text-sm">£</span>
                  <input
                    type="number"
                    min={0}
                    step="0.01"
                    value={customRate}
                    onChange={(e) => setCustomRate(e.target.value)}
                    placeholder="Leave blank for standard volume pricing"
                    className="w-full pl-7 pr-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-white text-sm placeholder-slate-600 focus:outline-none focus:border-teal-500/50"
                  />
                </div>
                <p className="text-[11px] text-slate-500 mt-1">
                  If set, every plan is billed at this flat rate (no volume tiers or size surcharge).
                </p>
              </div>
            </div>
          )}

          {error && (
            <div className="flex items-center gap-2 text-red-400 text-xs bg-red-500/10 rounded-lg px-3 py-2">
              <AlertCircle size={13} /> {error}
            </div>
          )}

          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="w-full py-2.5 rounded-lg font-semibold text-white bg-teal-600 hover:bg-teal-500 disabled:opacity-50 transition-all flex items-center justify-center gap-2 text-sm"
          >
            {saving ? (
              <>
                <Loader2 size={15} className="animate-spin" /> Saving…
              </>
            ) : saved ? (
              <>
                <Check size={15} /> Saved
              </>
            ) : (
              'Save billing settings'
            )}
          </button>
        </div>
      )}
    </div>
  );
};

export default UserBillingCard;
